import { makeStyles, Typography, Tab as MaterialUITab } from '@material-ui/core';
import { AttributesMixin, define, Events, refTo, Store, type } from '@type-r/models';
import { Linked, useEvent, useIO, useModel } from '@type-r/react';
import { asCurrency, asShortCurrency, div, Flex, FullWidthTabs, Loading, Page, PageContainer, PageTab, SearchInput, SelectSortBy, SmallTile, Tab, TilesList, Toolbar, TransparentTabs, useLinkedLocation } from 'lqx-react';
import * as React from 'react';
import { CompanyModel, globalStore, PlatformCompany } from 'api';
import { matchString } from 'api/common';
import { CustomerDebth, DebthByCategory, DebthSummary } from './api';
import { CustomerDebthList } from './invoicesList';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';


export const InvoicesPage = () => {
    const $category = useLinkedLocation(3, 'all');
    const state = useModel( DebthSummary );

    useIO( () => state.fetch(), [ $category.value ] );

    return (
        (Can(UserEntitlementCatalogs.BankEntryView) ||
                Can(UserEntitlementCatalogs.BankEntryWrite) ||
                Can(UserEntitlementCatalogs.BankEntryConfirm)) &&
        <PageContainer
            header={
                <InvoicesPageTabs $category={ $category } summary={ state } />
            }
        >
            <FutureInvoices $age={ $category }/> :
        </PageContainer>
    )
}

const InvoicesPageTabs = ({ $category, summary }: {
    $category : Linked<string>
    summary: DebthSummary
}) => {

    const classes = useStyles();

    return (
        <TransparentTabs $value={$category}>
            <PageTab value="all" title="All" label={asShortCurrency(summary.all.outstandingAmount, summary.all.currency )}/>
            <MaterialUITab
                aria-hidden="true"
                key="disabled"
                value={"separator"}
                disabled
                classes={{
                root: classes.separator
                }}
                label={<span className={classes.separatorElement} />}
            />
            <PageTab value="alldue" title="All Current" label={asShortCurrency(summary.alldue.outstandingAmount, summary.alldue.currency)}/>
            <PageTab value="day1" title="1-5 days" label={asShortCurrency(summary.day1.outstandingAmount, summary.day1.currency )}/>
            <PageTab value="day6" title="6-10 days" label={asShortCurrency(summary.day6.outstandingAmount, summary.day6.currency )}/>
            <MaterialUITab
                aria-hidden="true"
                key="disabled"
                value={"separator"}
                disabled
                classes={{
                root: classes.separator
                }}
                label={<span className={classes.separatorElement} />}
            />
            <PageTab value="past5days" title="1-5 days past" label={asShortCurrency(summary.past5days.outstandingAmount, summary.past5days.currency)}/>
            <PageTab value="past30days" title="6-30 days past" label={asShortCurrency(summary.past30days.outstandingAmount, summary.past30days.currency)}/>
            <PageTab value="past60days" title="31-60 days past" label={asShortCurrency(summary.past60days.outstandingAmount, summary.past60days.currency)}/>
            <PageTab value="past60plusdays" title="61+ days past" label={asShortCurrency(summary.past60plusdays.outstandingAmount, summary.past60plusdays.currency)}/>
        </TransparentTabs>
    )
}

@define
class FutureInvoicesState extends Store {
    static attributes = {
        companies : [ PlatformCompany ],
        customerDebths : [ CustomerDebth ],
        selected: refTo( CustomerDebth ),
        summaryByCategory : DebthByCategory,
        filter : '',
        category : 'all',
        sortBy: type({
            name: "outstandingAmountOrInvoiceAmount",
            isAsc: false
        }),
    }

    async fetchAll( range, category ){
        this.selected = null;

        await Promise.all([
            this.customerDebths.fetch({
                params : {
                    range,
                    status : category
                }
            }),
            this.summaryByCategory.fetch({
                params : { range }
            }),
        ]);
        this.companies = globalStore.platformCompanies;
    }
}
interface FutureInvoicesState extends AttributesMixin<typeof FutureInvoicesState>{}

const sortOptions = {
    outstandingAmountOrInvoiceAmount: "AR balance amount",
    customerName: "Company Name",
    overdueAmount: "Over Due Amount",
};

const FutureInvoices = ({ $age }) => {
    const state = useModel( FutureInvoicesState );

    const [filtered, setFiltered] = React.useState([]);

    const isReady = useIO( async () => {
        await state.fetchAll( $age.value, state.category );
    }, [ $age.value, state.category ])

    React.useMemo(async () => {
        const { name, isAsc } = state.sortBy;

        let filteredByInvoiceNumber = [];
        if (state.filter) {
            filteredByInvoiceNumber = await CustomerDebth.search({
                range: $age.value,
                status: state.category,
                invoiceNumberSearch: state.filter
            });
        }
        
        const invoices = state.customerDebths.filter(cd => cd.filter(state.filter, filteredByInvoiceNumber))
            .sort(CustomerDebth.comparator(name as any, isAsc));

            setFiltered(invoices);
    }, [state.customerDebths['_changeToken'], state.sortBy['_changeToken'], state.filter]);

    useEvent(Events, "futureInvoicesSummary:refresh", () => {
        state.summaryByCategory.fetch({
            params : { range: $age.value }
        });
    });

    React.useEffect(() => {
        state.$.filter.set('');
    }, [$age.value]);

    React.useEffect(() => {
        state.$.category.value === 'reconciled' && state.sortBy.$.name.set('customerName')
    }, [state.$.category.value])
    return (
        <Page
            menuWidth={530}
            maxWidth="xl"
            left={ 
                <>
                    <FilterByStatus $category={ state.$.category } summaryByCategory={ state.summaryByCategory } />
                    <Toolbar>
                        <SearchInput $value={ state.$.filter }/>
                    </Toolbar>
                    <Flex row>
                        <SelectSortBy
                            $attr={state.sortBy.$.name}
                            $asc={state.sortBy.$.isAsc}
                            options={state.$.category.value !== 'reconciled' ? sortOptions : {customerName: "Company Name"} }
                        />
                    </Flex>
                    <VerticalSpace />
                    
                    { isReady ? (
                        filtered.length ? (
                            <TilesList
                                items={filtered}
                                itemSize={64}
                                renderer={CustomerDebthTile( state.$.selected )}
                                selectedItem={state.selected}
                            />
                        ) : (
                            <Flex center stretch>
                                <Typography variant="h4">No results found.</Typography>
                            </Flex>
                        )
                    ):
                        <Loading />
                    }
                </>
            }>
                { state.selected ?
                    <CustomerDebthList customerDebth={state.selected} range={ $age.value } status={state.category} companies={state.companies} />
                :
                    <Flex center stretch>
                        Select the customer
                    </Flex>
                }
            </Page>
    )
}

const VerticalSpace = div({
    height: 8
})

const FilterByStatus = ({ $category, summaryByCategory } : {
    $category : Linked<string>
    summaryByCategory : DebthByCategory
}) => {
    return (
        <FullWidthTabs $value={$category}>
            <Tab value="all" title="All">{summaryByCategory.all.count}</Tab>
            <Tab value="new" title="New">{summaryByCategory.new.count}</Tab>
            <Tab value="promised" title="Promised">{summaryByCategory.promised.count}</Tab>
            <Tab value="withRA" title="w/RA">{summaryByCategory.withRA.count}</Tab>
            <Tab value="matched" title="Matched">{summaryByCategory.matched.count}</Tab>
            <Tab value="reconciled" title="Reconciled">{summaryByCategory.reconciled.count}</Tab>
        </FullWidthTabs>
    )
}

const CustomerDebthTile = $selected => ({ data, style, index }) => {
    const debth = data.items[ index ] as CustomerDebth;

    return (
        <div style={style}>
            <SmallTile
                color={'green'}
                isSelected={ $selected.value === debth }
                onClick={e => {
                    $selected.set(debth);
                }}
                footer={
                    <>
                    <div>{debth.customerName}</div>
                    <Flex grow />  
                    </>
                }
            >
                {asCurrency( debth.outstandingAmount || debth.invoicedAmount, debth.currency )} ({ debth.count })
                <Flex grow />
            </SmallTile>
        </div>
    )
}

const useStyles = makeStyles({
    separator: {
      width: 35,
      minWidth: 20
    },
  
    separatorElement: {
      borderLeft: "solid",
      borderWidth: 1,
      borderColor: "white",
      height: 41,
      marginTop: 7
    }
  });